// src/utils/locationResolution.ts
import type { FreshaLocation } from '../types/fresha.ts';
import type { LocationNameMode } from '../types/settings.ts';

/**
 * Universal brand-agnostic resolver for location / sede names with 4-layer fallback:
 *
 * Layer 1: User-configured Alias in settings.locationAliases (if mode === 'alias' and alias is non-empty)
 * Layer 2: Active Fresha metadata:
 *          - If mode === 'public' -> loc.attributes.name || loc.attributes['internal-name']
 *          - If mode === 'internal' -> loc.attributes['internal-name'] || loc.attributes.name
 *          - If mode === 'alias' (no alias set) -> loc.attributes['internal-name'] || loc.attributes.name || loc.name
 * Layer 3: Immutable appointment snapshot fallbackName (e.g. from historical appointment/sale)
 * Layer 4: Graceful placeholder for closed/deleted branches: "Sede #{locationId} (Inactiva)" or empty string
 */
export function resolveLocationName(
  locationId?: string | null,
  fallbackName?: string,
  locations: FreshaLocation[] = [],
  aliases: Record<string, string> = {},
  mode: LocationNameMode = 'alias'
): string {
  const locIdStr = locationId ? String(locationId).trim() : '';

  // Layer 1: Alias override
  if (mode === 'alias' && locIdStr && aliases[locIdStr]?.trim()) {
    return aliases[locIdStr].trim();
  }

  // Layer 2: Find in active Fresha locations list
  if (locIdStr && locations.length > 0) {
    const matched = locations.find((l) => String(l.id) === locIdStr);
    if (matched) {
      const internalName =
        matched.attributes?.['internal-name'] ||
        (matched.attributes as any)?.internalName ||
        (matched as any)['internal-name'] ||
        (matched as any).internalName ||
        '';

      const publicName =
        matched.attributes?.name ||
        (matched as any).name ||
        '';

      if (mode === 'public') {
        return publicName || internalName || fallbackName || '';
      }

      // Default / internal: prefer internal-name if present (usually "Naguanagua", "Viña"), otherwise public name
      return internalName || publicName || fallbackName || '';
    }
  }

  // Layer 3: Preserve original snapshot fallback name
  if (fallbackName && fallbackName.trim()) {
    return fallbackName.trim();
  }

  // Layer 4: Inactive branch placeholder if location ID was provided
  if (locIdStr) {
    return `Sede #${locIdStr} (Inactiva)`;
  }

  return '';
}

/**
 * Pre-builds a Map of locationId -> resolvedName for fast batch lookups.
 */
export function buildLocationMap(
  locations: FreshaLocation[] = [],
  aliases: Record<string, string> = {},
  mode: LocationNameMode = 'alias'
): Map<string, string> {
  const map = new Map<string, string>();
  for (const loc of locations) {
    if (!loc?.id) continue;
    const id = String(loc.id);
    const resolved = resolveLocationName(id, undefined, locations, aliases, mode);
    if (resolved) {
      map.set(id, resolved);
    }
  }
  return map;
}
